import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Outlet, NavLink } from "react-router-dom";
import {
  LogOut, Bell, Search, ArrowLeftRight, Menu, X, PanelLeftClose, PanelLeftOpen, ChevronDown,
  Settings, Info, PhoneMissed, Rocket, CheckSquare, UserPlus, AlertTriangle, Mail, ShieldAlert,
} from "lucide-react";
import { useAuth } from "@/context/CrmAuth";
import { TwilioProvider } from "@/context/TwilioVoice";
import api from "@/lib/crmApi";
import { cn } from "@/lib/utils";
import IncomingCallPopup from "@/crm/components/telephony/IncomingCallPopup";
import SoftphoneBar from "@/crm/components/telephony/SoftphoneBar";
import CopilotHost from "@/crm/components/copilot/CopilotHost";
import { FloatingWindowsProvider } from "@/crm/components/FloatingWindow";
import RealtimeAlerts from "@/crm/components/RealtimeAlerts";
import SectorSwitcher from "@/crm/components/SectorSwitcher";
import OrganizationSwitcher from "@/crm/components/OrganizationSwitcher";
import SidebarNav from "@/crm/components/SidebarNav";
import GlobalSearch from "@/crm/components/GlobalSearch";
import Logo, { Wordmark } from "@/crm/components/Logo";
import { bottomNavItems } from "@/crm/navigation";

const ROLE_LABELS = { admin: "Administrateur", manager: "Manager", commercial: "Commercial" };
const RAIL_KEY = "prospee_nav_rail";

// Barre réduite : préférence persistée par utilisateur. localStorage peut lever
// (navigation privée) — on retombe sur la barre étendue.
const readRail = () => { try { return localStorage.getItem(RAIL_KEY) === "1"; } catch { return false; } };

// Icône et teinte d'une notification selon son `kind` (backend crm/utils.notify).
const NOTIF_KINDS = {
  info: { icon: Info, bg: "#e8f1fd", fg: "#005bd3" },
  call: { icon: PhoneMissed, bg: "#fdecea", fg: "#b42318" },
  campaign: { icon: Rocket, bg: "#e8f1fd", fg: "#005bd3" },
  task: { icon: CheckSquare, bg: "#e7f5f1", fg: "#005c48" },
  prospect: { icon: UserPlus, bg: "#f3e8ff", fg: "#7c3aed" },
  email: { icon: Mail, bg: "#eaf4ff", fg: "#00527c" },
  warning: { icon: AlertTriangle, bg: "#fdf3e7", fg: "#8a6116" },
};

// « il y a 4 min », « hier, 17:40 », « 12/08, 09:05 » — assez pour situer une
// notification sans lire une date complète.
function notifTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24 && d.getDate() === new Date().getDate()) return `il y a ${h} h`;
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `hier, ${time}`;
  return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}, ${time}`;
}

const initials = (name) => (name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2)
  .map((p) => p[0]).join("").toUpperCase() || "?";
// « Camille Bernard » → « Camille B. » : le prénom suffit dans l'en-tête.
const shortName = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || "";
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

const FOCUS = "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#005bd3]";
const FOCUS_DARK = "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4f9dff]";
const HEADER_ICON_BTN = cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-[11px] border border-transparent text-[#303030] transition-colors hover:bg-[#f2f4f7]", FOCUS);
const PANEL = "absolute right-0 top-[52px] z-[60] rounded-[14px] border border-[#e3e3e3] bg-white shadow-[0_24px_48px_-18px_rgba(10,12,24,.35)] animate-pop-in";

/**
 * Coquille du CRM : barre latérale, en-tête, barre d'onglets mobile, et les
 * surfaces globales (softphone, copilote, fenêtres flottantes, alertes).
 *
 * Paliers responsives (tailwind.config.js, `shell-*`) — en CSS, sans mesure
 * JS, pour que le premier rendu soit déjà juste :
 *
 *   ≥ 1080   menu étendu 248 px (ou rail si l'utilisateur l'a réduit)
 *   900–1079 rail d'icônes 76 px, en-tête sans ⌘K
 *   760–899  rail, en-tête compact (secteur et nom d'utilisateur masqués)
 *   < 760    tiroir hors-champ + barre d'onglets en bas
 *
 * Deux variables CSS posées sur la racine sont lues par les surfaces fixes :
 *   --sidebar-w     largeur courante de la barre latérale (softphone, fenêtres)
 *   --bottom-nav-h  hauteur de la barre d'onglets mobile (0 dès 760 px), pour
 *                   que le softphone et les barres d'action se posent dessus.
 */
export default function CrmLayout() {
  const { user, logout, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [showNotif, setShowNotif] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [bellPulse, setBellPulse] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [rail, setRail] = useState(readRail);
  const [searchOpen, setSearchOpen] = useState(false);
  const menusRef = useRef(null);

  // ⌘K / Ctrl+K : raccourci attendu d'une recherche globale. Ignoré quand la
  // frappe part d'un champ, pour ne pas voler un raccourci du navigateur au
  // milieu d'une saisie.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        const tag = (e.target?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const loadNotifs = () => api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});

  const handleAlertPing = () => {
    loadNotifs();
    setBellPulse(true);
    setTimeout(() => setBellPulse(false), 1300);
  };

  useEffect(() => {
    if (!user) return;
    const load = () => {
      api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
      api.post("/presence/ping").catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  // Le tiroir mobile et les menus se referment à chaque navigation : sans ça
  // ils resteraient ouverts par-dessus la page qu'on vient d'ouvrir.
  useEffect(() => { setMobileNav(false); setShowNotif(false); setShowAccount(false); }, [pathname]);

  // Échap referme tiroir, notifications et menu du compte (la palette de
  // recherche gère la sienne). Verrou de défilement du fond tant que le
  // tiroir est ouvert.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setMobileNav(false); setShowNotif(false); setShowAccount(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!mobileNav) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [mobileNav]);

  // Un clic hors des panneaux de l'en-tête les referme.
  useEffect(() => {
    if (!showNotif && !showAccount) return;
    const onDown = (e) => {
      if (menusRef.current && !menusRef.current.contains(e.target)) { setShowNotif(false); setShowAccount(false); }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showNotif, showAccount]);

  const toggleRail = () => setRail((r) => {
    try { localStorage.setItem(RAIL_KEY, r ? "0" : "1"); } catch { /* non bloquant */ }
    return !r;
  });

  // Le Live Copilot occupe le flanc droit : quand il s'ouvre, le menu se
  // replie en rail pour laisser la place à la fiche ; quand il se ferme, la
  // préférence enregistrée de l'utilisateur reprend. Rien n'est persisté ici :
  // c'est un repli de circonstance, pas un choix.
  const onCopilotVisible = useCallback((visible) => setRail(visible ? true : readRail()), []);

  if (user === undefined) return null;

  // Fermeture d'une notification : retrait optimiste de la liste, puis
  // suppression serveur (sinon le prochain sondage la ferait réapparaître).
  const dismissNotif = (n) => {
    setNotifs((prev) => ({
      items: prev.items.filter((x) => x.id !== n.id),
      unread: n.read === false ? Math.max(0, prev.unread - 1) : prev.unread,
    }));
    api.delete(`/notifications/${n.id}`).catch(loadNotifs);
  };

  const markAllRead = () => {
    api.post("/notifications/read")
      .then(() => setNotifs((n) => ({ unread: 0, items: n.items.map((x) => ({ ...x, read: true })) })))
      .catch(() => {});
  };

  const openNotif = () => {
    setShowAccount(false);
    setShowNotif((s) => !s);
    if (!showNotif && notifs.unread) markAllRead();
  };
  const openAccount = () => { setShowNotif(false); setShowAccount((s) => !s); };

  const canSettings = user?.role === "admin" || user?.role === "manager";
  const bottomItems = bottomNavItems(user?.role);

  return (
    <TwilioProvider>
    {/* Les fenêtres flottantes (préparation d'appel, copilote) partagent une
        barre des tâches et la règle « une seule déployée sur petit écran » : le
        fournisseur doit donc englober AUSSI BIEN les pages (<Outlet/>, d'où
        vient la préparation) que le copilote monté ici. */}
    <FloatingWindowsProvider>
    <div
      className={cn(
        "min-h-screen bg-[#f9fafb] text-[#0f172a]",
        "[--bottom-nav-h:69px] shell-sm:[--bottom-nav-h:0px] [--sidebar-w:76px]",
        !rail && "shell-lg:[--sidebar-w:248px]",
      )}
      data-testid="crm-shell"
    >
      {/* Voile du tiroir mobile */}
      <div
        onClick={() => setMobileNav(false)}
        aria-hidden="true"
        className={cn(
          "fixed inset-0 z-40 bg-[rgba(11,21,36,.55)] backdrop-blur-[2px] transition-opacity duration-200 shell-sm:hidden",
          mobileNav ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      />

      {/* Barre latérale — tiroir sous 760 px, rail ou menu étendu au-dessus */}
      <aside
        id="sidebar"
        data-testid="sidebar"
        data-open={mobileNav ? "true" : "false"}
        data-rail={rail ? "true" : "false"}
        aria-label="Navigation principale"
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-[min(300px,86%)] flex-col bg-[#0f172a] text-[#94a3b8]",
          "transition-[transform,width,box-shadow] duration-300 ease-out",
          mobileNav ? "translate-x-0 shadow-[0_24px_60px_rgba(3,7,18,.5)]" : "-translate-x-full",
          "shell-sm:w-[var(--sidebar-w)] shell-sm:translate-x-0 shell-sm:shadow-none"
        )}
      >
        <div className={cn("flex h-16 shrink-0 items-center gap-2.5 border-b border-[#1e293b] px-3.5",
          "shell-sm:justify-center", !rail && "shell-lg:justify-start")}>
          <Logo size={26} variant="dark" className="shrink-0" />
          <Wordmark variant="dark" className={cn("min-w-0 flex-1 truncate text-[17px] text-white",
            rail ? "shell-sm:hidden" : "shell-sm:hidden shell-lg:block")} />
          <button
            type="button"
            onClick={() => setMobileNav(false)}
            data-testid="close-nav-btn"
            aria-label="Fermer le menu"
            className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-[#94a3b8] transition-colors hover:bg-[#1e293b] hover:text-white shell-sm:hidden", FOCUS_DARK)}
          >
            <X size={18} />
          </button>
        </div>

        <SidebarNav role={user?.role} railPref={rail} onNavigate={() => setMobileNav(false)} />

        <div className="flex shrink-0 flex-col gap-2 border-t border-[#1e293b] p-3">
          <div className={cn("flex min-h-[44px] items-center gap-2.5 px-1", "shell-sm:justify-center shell-sm:px-0",
            !rail && "shell-lg:justify-start shell-lg:px-1")}>
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#005bd3] text-sm font-semibold text-white"
              title={user?.name}>
              {(user?.name || "?")[0]}
            </span>
            <div className={cn("min-w-0 flex-1", rail ? "shell-sm:hidden" : "shell-sm:hidden shell-lg:block")}>
              <div className="truncate text-[13px] font-medium text-white">{user?.name}</div>
              <div className="text-[11px] text-[#64748b]">{ROLE_LABELS[user?.role]}</div>
            </div>
            <button type="button" data-testid="logout-btn" onClick={logout} title="Déconnexion" aria-label="Déconnexion"
              className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-[#64748b] transition-colors hover:bg-[#1e293b] hover:text-white",
                FOCUS_DARK, rail ? "shell-sm:hidden" : "shell-sm:hidden shell-lg:flex")}>
              <LogOut size={17} />
            </button>
          </div>

          {/* Réduction de la barre : desktop uniquement (≥ 1080 px). */}
          <button
            type="button"
            onClick={toggleRail}
            data-testid="rail-toggle"
            aria-pressed={rail}
            title={rail ? "Étendre le menu" : "Réduire le menu"}
            className={cn("hidden min-h-[38px] w-full items-center justify-center gap-[7px] rounded-[10px] border border-[#22304a] text-xs text-[#94a3b8] transition-colors hover:border-[#3d5170] hover:text-white shell-lg:flex", FOCUS_DARK)}
          >
            {rail ? <PanelLeftOpen size={15} /> : <><PanelLeftClose size={15} /> Réduire le menu</>}
          </button>
        </div>
      </aside>

      {/* Contenu */}
      <div className="flex min-h-screen min-w-0 flex-col transition-[margin] duration-300 ease-out shell-sm:ml-[var(--sidebar-w)]">
        {user?.impersonated_by && (
          <div className="flex shrink-0 flex-wrap items-center justify-center gap-x-2 gap-y-0.5 bg-[#8a6116] px-4 py-2 text-sm font-medium text-white" data-testid="impersonation-banner">
            <ArrowLeftRight size={15} className="shrink-0" />
            <span>Connecté en tant que <b>{user.name}</b> (par {user.impersonated_by.name})</span>
            <button data-testid="stop-impersonation-btn" onClick={() => stopImpersonation()}
              className="underline underline-offset-2 hover:no-underline">
              Retour à mon compte admin
            </button>
          </div>
        )}

        {/* Visite plateforme : l'exploitant travaille chez un client dont il
            n'est pas membre (plan V4 §46). L'entrée a été motivée et tracée
            dans le journal d'audit du client ; le bandeau, lui, empêche qu'on
            l'oublie — c'est la donnée de quelqu'un d'autre à l'écran. */}
        {user?.platform_visit && (
          <div className="flex shrink-0 flex-wrap items-center justify-center gap-x-2 gap-y-0.5 bg-[#7c3aed] px-4 py-2 text-sm font-medium text-white"
            data-testid="platform-visit-banner">
            <ShieldAlert size={15} className="shrink-0" />
            <span>
              Accès plateforme : vous consultez les données de{" "}
              <b>{user.organization?.name}</b>, dont vous n'êtes pas membre. Cet accès est journalisé.
            </span>
          </div>
        )}

        <header className="sticky top-0 z-30 flex h-[58px] shrink-0 items-center gap-2 border-b border-[#e3e3e3] bg-white px-3 shell-sm:h-16">
          <button
            type="button"
            onClick={() => setMobileNav(true)}
            data-testid="open-nav-btn"
            aria-label="Ouvrir le menu"
            aria-controls="sidebar"
            aria-expanded={mobileNav}
            className={cn(HEADER_ICON_BTN, "shell-sm:hidden")}
          >
            <Menu size={21} />
          </button>

          <span className="flex min-w-0 items-center gap-2 shell-sm:hidden">
            <Logo size={22} className="shrink-0" />
            <Wordmark className="truncate text-[15px] text-[#16171a]" />
          </span>

          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            data-testid="global-search-btn"
            aria-label="Rechercher une entreprise"
            className={cn(
              "flex h-[42px] min-w-[44px] flex-[0_0_44px] items-center justify-center gap-[9px] rounded-[11px] border border-[#e3e3e3] bg-[#f7f8fa] px-0 text-sm text-[#94a3b8] transition-all duration-150",
              "hover:border-[#d0d0d0] hover:bg-[#f2f4f7] hover:text-[#64748b]",
              "shell-sm:max-w-[380px] shell-sm:flex-[1_1_320px] shell-sm:justify-start shell-sm:px-3",
              FOCUS,
            )}
          >
            <Search size={17} className="shrink-0" />
            <span className="hidden min-w-0 flex-1 truncate text-left shell-sm:inline">Rechercher une entreprise…</span>
            <kbd className="hidden shrink-0 rounded-md border border-[#e3e3e3] bg-white px-1.5 py-0.5 font-mono text-[11px] text-[#94a3b8] shell-lg:inline">⌘K</kbd>
          </button>

          <div ref={menusRef} className="relative ml-auto flex items-center gap-1.5">
            <div className="hidden shell-md:block"><OrganizationSwitcher /></div>
            <div className="hidden shell-md:block"><SectorSwitcher /></div>

            <button type="button" data-testid="notif-btn" onClick={openNotif} aria-label="Notifications" aria-expanded={showNotif}
              className={cn(HEADER_ICON_BTN, "relative")}>
              <Bell size={20} className={cn(bellPulse && "animate-bell-shake text-[#005bd3]")} />
              {notifs.unread > 0 && (
                <span className="absolute right-[7px] top-[7px] flex h-[17px] min-w-[17px] items-center justify-center rounded-full border-2 border-white bg-[#d72c0d] px-1 font-mono text-[10px] text-white">
                  {notifs.unread}
                </span>
              )}
            </button>

            <button type="button" data-testid="account-btn" onClick={openAccount} aria-label="Mon compte" aria-expanded={showAccount}
              className={cn("flex h-11 items-center gap-2 rounded-[11px] border border-transparent px-1.5 transition-colors hover:bg-[#f2f4f7]", FOCUS)}>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#e8f1fd] text-[13px] font-semibold text-[#005bd3]">
                {initials(user?.name)}
              </span>
              <span className="hidden text-left shell-md:block">
                <span className="block text-[13px] font-semibold leading-tight text-[#0f172a]">{shortName(user?.name)}</span>
                <span className="block text-[11px] text-[#94a3b8]">{ROLE_LABELS[user?.role]}</span>
              </span>
              <ChevronDown size={14} className="hidden text-[#94a3b8] shell-md:block" />
            </button>

            {showNotif && (
              <div role="dialog" aria-label="Notifications" data-testid="notif-panel"
                className={cn(PANEL, "flex max-h-[min(420px,calc(100vh-80px))] w-[min(340px,calc(100vw-2rem))] flex-col overflow-hidden")}>
                <div className="flex shrink-0 items-center justify-between border-b border-[#ebebeb] px-3.5 py-3">
                  <span className="text-[11px] font-bold uppercase tracking-[.08em] text-[#94a3b8]">Notifications</span>
                  <button
                    type="button"
                    onClick={() => setShowNotif(false)}
                    data-testid="notif-panel-close"
                    aria-label="Fermer les notifications"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-[#f2f4f7]"
                  >
                    <X size={15} />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                  {notifs.items.length === 0 ? (
                    <p className="p-4 text-center text-[13px] text-[#94a3b8]">Aucune notification</p>
                  ) : notifs.items.map((n) => {
                    const k = NOTIF_KINDS[n.kind] || NOTIF_KINDS.info;
                    return (
                      <div key={n.id} data-testid={`notif-item-${n.id}`} className="group flex gap-2.5 rounded-[10px] p-2.5 hover:bg-[#fafafa]">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px]" style={{ background: k.bg, color: k.fg }}>
                          <k.icon size={16} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className={cn("text-[13px] leading-[1.45] text-[#303030]", n.read === false && "font-medium")}>{n.message}</div>
                          <div className="mt-0.5 text-[11px] text-[#94a3b8]">{notifTime(n.created_at)}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => dismissNotif(n)}
                          data-testid={`notif-dismiss-${n.id}`}
                          aria-label="Fermer cette notification"
                          className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#cbd5e1] transition-colors hover:bg-[#f2f4f7] hover:text-[#64748b]"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button type="button" onClick={markAllRead} data-testid="notif-mark-read"
                  className="w-full shrink-0 border-t border-[#ebebeb] bg-[#fafafa] py-[11px] text-[13px] font-medium text-[#005bd3] transition-colors hover:bg-[#f2f4f7]">
                  Tout marquer comme lu
                </button>
              </div>
            )}

            {showAccount && (
              <div role="menu" aria-label="Mon compte" data-testid="account-menu" className={cn(PANEL, "w-[230px] p-1.5")}>
                <div className="px-2.5 pb-2 pt-1.5">
                  <div className="truncate text-[13px] font-semibold text-[#0f172a]">{user?.name}</div>
                  <div className="truncate text-[11px] text-[#94a3b8]">{user?.email || ROLE_LABELS[user?.role]}</div>
                </div>
                {canSettings && (
                  <button type="button" role="menuitem" data-testid="account-settings" onClick={() => navigate("/parametres")}
                    className={cn("flex min-h-[42px] w-full items-center gap-2.5 rounded-[10px] px-2.5 text-left text-sm text-[#303030] transition-colors hover:bg-[#f7f8fa]", FOCUS)}>
                    <Settings size={16} className="text-[#64748b]" /> Préférences
                  </button>
                )}
                {/* Sous 900 px le filtre sectoriel quitte l'en-tête : il reste
                    accessible ici. */}
                {canSettings && (
                  <div className="px-1 py-1 shell-md:hidden" role="none">
                    <OrganizationSwitcher className="h-10 w-full rounded-[10px]" />
                    <SectorSwitcher className="h-10 w-full rounded-[10px]" />
                  </div>
                )}
                <button type="button" role="menuitem" data-testid="account-logout" onClick={logout}
                  className={cn("flex min-h-[42px] w-full items-center gap-2.5 rounded-[10px] px-2.5 text-left text-sm text-[#d72c0d] transition-colors hover:bg-[#fdecea]", FOCUS)}>
                  <LogOut size={16} /> Déconnexion
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Réserve basse : 96 px pour la barre du softphone (fixed bottom-0),
            plus la barre d'onglets mobile sous 760 px. */}
        {/* `animate-fade-in` et non `animate-slide-up` : cette dernière se termine
            sur `transform: translateY(0)` conservé par son fill-mode `both`, et
            un transform — même nul — fait de l'élément le bloc conteneur de tous
            ses descendants `position: fixed`. <main> devenait donc la référence
            des voiles de modales et des panneaux flottants : centrés sur la
            hauteur de la page au lieu de l'écran, ils s'affichaient tout en bas.
            L'entrée de page ne joue plus que sur l'opacité. */}
        <main className="mx-auto w-full max-w-[1600px] flex-1 px-[14px] pb-[calc(6rem_+_var(--bottom-nav-h,0px))] pt-[14px] animate-fade-in shell-sm:px-[22px] shell-sm:pt-[22px]">
          <Outlet />
        </main>
      </div>

      {/* Barre d'onglets mobile (< 760 px) : quatre écrans de tête + Menu. */}
      <nav aria-label="Navigation rapide" data-testid="bottom-nav"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-[#e3e3e3] bg-white/[.94] px-1.5 pb-2.5 pt-1.5 backdrop-blur-[10px] shell-sm:hidden">
        <div className="grid grid-cols-5 gap-0.5">
          {bottomItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={`bottom-nav-${item.to === "/" ? "accueil" : item.to.slice(1)}`}
              className={({ isActive }) => cn(
                "flex min-h-[52px] flex-col items-center justify-center gap-[3px] rounded-xl px-0.5 py-1 transition-colors",
                "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#005bd3]",
                isActive ? "bg-[#e8f1fd] text-[#005bd3]" : "text-[#64748b]"
              )}>
              {({ isActive }) => (
                <>
                  <item.icon size={21} />
                  <span className={cn("whitespace-nowrap text-[10.5px]", isActive ? "font-semibold" : "font-medium")}>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
          <button type="button" onClick={() => setMobileNav(true)} data-testid="bottom-nav-menu" aria-controls="sidebar" aria-expanded={mobileNav}
            className={cn("flex min-h-[52px] flex-col items-center justify-center gap-[3px] rounded-xl px-0.5 py-1 text-[#64748b] transition-colors",
              "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#005bd3]",
              mobileNav && "bg-[#e8f1fd] text-[#005bd3]")}>
            <Menu size={21} />
            <span className="whitespace-nowrap text-[10.5px] font-medium">Menu</span>
          </button>
        </div>
      </nav>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <IncomingCallPopup />
      <SoftphoneBar />
      <CopilotHost onVisibleChange={onCopilotVisible} />
      <RealtimeAlerts onPing={handleAlertPing} />
    </div>
    </FloatingWindowsProvider>
    </TwilioProvider>
  );
}
